import React, { useContext, useMemo, useState } from 'react'

// @mui material components
import { Autocomplete, Icon, IconButton, TextField } from '@mui/material'
import Card from '@mui/material/Card'
import Grid from '@mui/material/Grid'
import { saveAs } from 'file-saver'
import { useNavigate } from 'react-router-dom'

import MDAvatar from 'components/MDAvatar'
import MDBadge from 'components/MDBadge'
import MDBox from 'components/MDBox'
import MDButton from 'components/MDButton'
import MDInput from 'components/MDInput'
import MDTypography from 'components/MDTypography'
import { ROLE_LEVEL } from 'constants/roles'
import { useAuth } from 'context/auth'
import { useMaterialUIController } from 'context/materialUI'
import {
	ALLOWED_ATTACHMENT_INPUT_TYPES,
	ALLOWED_FILE_TYPES,
	MAX_FILE_SIZE_BYTES,
	MAX_FILE_SIZE_MB,
} from 'layouts/feedback/constants'
import { RemoteFile } from 'layouts/feedback/types'
import { getFilenameFromFirebaseUrl } from 'layouts/feedback/utils'
import FormField from 'modules/FormField'
import { ROUTE_PATHS } from 'routes'

import { FormContext } from '../../constants'
import { Feedback } from '../../types'

function Details(): JSX.Element {
	const [controller] = useMaterialUIController()
	const { user } = useAuth()
	const formContext = useContext(FormContext)
	const { isNew, localForm, setFormEdited, setLocalForm, showError } = formContext
	const [uploadError, setUploadError] = useState<string | null>(null)
	const navigate = useNavigate()

	const { orgMembers, configuration } = controller

	const now = new Date()

	const STAGE = 1
	const errorVisible = localForm.stage.current >= STAGE && showError

	const sectionDisabled =
		!ROLE_LEVEL[2].includes(user.data.role) && localForm.stage.current !== STAGE

	const categoryOptions = useMemo(
		() =>
			configuration?.feedback?.categories.filter(
				item => item.memberType === localForm.memberType && item.feedbackType === localForm.type
			),
		[localForm.memberType, localForm.type, configuration?.feedback?.categories]
	)

	const categoryNameMapping = useMemo(
		() => Object.fromEntries(categoryOptions?.map(c => [c.name, { ...c }]) ?? []),
		[categoryOptions]
	)

	const filteredMembers = useMemo(
		() => orgMembers?.filter(member => member.memberType === localForm.memberType) ?? [],
		[localForm.memberType, orgMembers]
	)

	const orgMemberRefncIds = filteredMembers.map(member => member.referenceId)

	const orgMemberNames = filteredMembers.map(member => member.name)

	const memberRfncMapping = useMemo(
		() => Object.fromEntries(filteredMembers.map(member => [member.referenceId, { ...member }])),
		[filteredMembers]
	)

	const memberNameMapping = useMemo(
		() => Object.fromEntries(filteredMembers.map(member => [member.name, { ...member }])),
		[filteredMembers]
	)

	const handleFormUpdate = (value: any, field: keyof Feedback['form']['details']) => {
		setLocalForm(prev => ({
			...prev,
			form: {
				...prev.form,
				details: {
					...prev.form.details,
					[field]: value,
				},
			},
		}))
		setFormEdited(true)
	}

	const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0]
		if (!file) return

		if (!ALLOWED_FILE_TYPES.includes(file.type)) {
			setUploadError(`${file.name}: File type not supported.`)
			return
		}
		if (file.size > MAX_FILE_SIZE_BYTES) {
			setUploadError(
				`${file.name} must be less than ${MAX_FILE_SIZE_MB}MB. File size is ${(
					file.size /
					(1024 * 1024)
				).toFixed(1)}MB.`
			)
			return
		}
		handleFormUpdate(
			[...localForm.form.details.attachments, { type: 'local', file }],
			'attachments'
		)
		setUploadError(null)
		setFormEdited(true)
		event.target.value = '' // Reset file input to allow re-uploading the same file
	}

	const handleLocalDownload = (file: File) => {
		if (!file) return
		saveAs(file, file.name)
	}

	const handleRemoteDownload = (url: string, filename: string) => {
		if (!url) return
		saveAs(url, filename)
	}

	const deleteLocalAttachment = (file: File) => {
		if (!file) return

		const updatedAttachments = localForm.form.details.attachments.filter(
			attachment => attachment.type === 'local' && attachment.file !== file
		)
		handleFormUpdate(updatedAttachments, 'attachments')
		setFormEdited(true)
		setUploadError(null)
	}

	const markRemoteAttachmentForDeletion = (fileIndex: number) => {
		const updatedAttachments = [...localForm.form.details.attachments]
		updatedAttachments[fileIndex] = {
			...updatedAttachments[fileIndex],
			delete: true, // Mark the remote file for deletion
		} as RemoteFile

		handleFormUpdate(updatedAttachments, 'attachments')
		setFormEdited(true)
		setUploadError(null)
	}

	return (
		<Card id="details">
			<MDBox component="form" p={2}>
				<Grid container spacing={3} alignItems="center">
					<Grid item xs={12}>
						<MDBox
							display={'flex'}
							flexDirection={'row'}
							justifyContent={'space-between'}
							alignItems={'center'}
						>
							<MDBox display={'flex'} flexDirection={'row'} gap={2} alignItems={'center'}>
								<MDAvatar bgColor="primary" size="xs">
									1
								</MDAvatar>
								<MDTypography variant="h5" fontWeight="medium">
									Details
								</MDTypography>
							</MDBox>
							<MDBox display={'flex'} gap={2}>
								{!isNew && (
									<>
										{localForm.stage.min !== 6 && localForm.dueDatetime && (
											<MDTypography variant="body2">
												<i>Due: {new Date(localForm.dueDatetime).toDateString()}</i>
											</MDTypography>
										)}
										<MDBadge
											variant="contained"
											color={
												new Date(localForm.dueDatetime) < now && localForm.stage.min !== 6
													? 'error'
													: localForm.stage.min === 6
														? 'success'
														: 'info'
											}
											badgeContent={
												new Date(localForm.dueDatetime) < now && localForm.stage.min !== 6
													? 'OVERDUE'
													: localForm.stage.min === 6
														? 'COMPLETE'
														: 'PENDING'
											}
											container
										/>
									</>
								)}
							</MDBox>
						</MDBox>
					</Grid>
					{!isNew && (
						<Grid item xs={12} md={2}>
							<MDBox
								height="100%"
								mt={0.5}
								lineHeight={1}
								gap={1}
								display={'flex'}
								flexDirection={'column'}
							>
								<MDTypography variant="caption" fontWeight="regular" color="text">
									ID
								</MDTypography>
								<MDTypography variant="button" color="text" fontWeight="medium">
									{localForm.form?.details.id ?? ''}
								</MDTypography>
							</MDBox>
						</Grid>
					)}
					<Grid item xs={12} md={6}>
						<MDBox
							height="100%"
							mt={0.5}
							lineHeight={1}
							gap={1}
							display={'flex'}
							flexDirection={'column'}
						>
							<Autocomplete
								disableClearable
								readOnly={sectionDisabled}
								value={localForm.form.details.category?.name ?? null}
								options={categoryOptions?.map(item => item.name) ?? []}
								noOptionsText="No feedback categories set for this member"
								onChange={(_, newValue) => {
									const categoryDetails = categoryNameMapping[newValue]
									setLocalForm(prev => ({
										...prev,
										form: {
											...prev.form,
											details: {
												...prev.form.details,
												category: {
													...categoryDetails,
													name: newValue,
												},
											},
										},
									}))
									setFormEdited(true)
								}}
								size="medium"
								renderInput={params => (
									<FormField
										{...params}
										label="Category"
										error={
											showError &&
											localForm.stage.current === 1 &&
											!localForm.form.details.category?.name
										}
										helperText={
											showError &&
											localForm.stage.current === 1 &&
											!localForm.form.details.category?.name
												? 'Category is required'
												: ''
										}
									/>
								)}
							/>
						</MDBox>
					</Grid>
					<Grid item xs={12} md={4}>
						<MDBox
							height="100%"
							mt={0.5}
							lineHeight={1}
							gap={1}
							display={'flex'}
							flexDirection={'column'}
						>
							<MDTypography variant="caption" fontWeight="regular" color="text">
								Severity
							</MDTypography>
							<MDTypography variant="button" color="secondary" fontWeight="medium">
								{categoryOptions?.find(c => c.name === localForm.form.details.category?.name)
									?.severity ?? 'N/A'}
							</MDTypography>
						</MDBox>
					</Grid>
					<Grid item xs={12} sm={6} md={4}>
						<FormField
							label="Incident Date/Time"
							inputProps={{ type: 'datetime-local' }}
							disabled={sectionDisabled}
							value={localForm.form.details?.incidentDatetime ?? ''}
							onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
								handleFormUpdate(e.target.value, 'incidentDatetime')
							}}
							error={errorVisible && !localForm.form.details?.incidentDatetime}
							helperText={
								errorVisible && !localForm.form.details?.incidentDatetime
									? 'Date/Time is required'
									: ''
							}
						/>
					</Grid>
					<Grid item xs={12} sm={6} md={4}>
						<FormField
							label="Job Number"
							disabled={sectionDisabled}
							value={localForm.form.details?.jobNumber ?? ''}
							onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
								handleFormUpdate(e.target.value, 'jobNumber')
							}}
							error={errorVisible && !localForm.form.details?.jobNumber}
							helperText={
								errorVisible && !localForm.form.details?.jobNumber ? 'Job Number is required' : ''
							}
						/>
					</Grid>

					<Grid item xs={12} sm={6} md={4}>
						<FormField
							label="Booking Phone Number"
							disabled={sectionDisabled}
							value={localForm.form.details?.bookingPhoneNumber ?? ''}
							onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
								handleFormUpdate(e.target.value, 'bookingPhoneNumber')
							}}
							error={errorVisible && !localForm.form.details?.bookingPhoneNumber}
							helperText={
								errorVisible && !localForm.form.details?.bookingPhoneNumber
									? 'Booking Phone Number is required'
									: ''
							}
						/>
					</Grid>
					<Grid item xs={12} sm={4}>
						<Autocomplete
							options={orgMemberRefncIds}
							readOnly={sectionDisabled}
							noOptionsText="No organisation members set"
							renderInput={params => (
								<FormField
									{...params}
									label="Organisation Member ID"
									InputLabelProps={{ shrink: true }}
									error={errorVisible && !localForm.form.details?.orgMember}
									helperText={
										errorVisible && !localForm.form.details?.orgMember
											? 'Booking Phone Number is required'
											: ''
									}
								/>
							)}
							value={localForm.form.details?.orgMember?.referenceId ?? null}
							onChange={(_, newValue) => {
								const memberDetails = memberRfncMapping[newValue]
								setLocalForm(prev => ({
									...prev,
									form: {
										...prev.form,
										details: {
											...prev.form.details,
											orgMember: {
												...memberDetails,
												referenceId: newValue,
											},
										},
									},
								}))
								setFormEdited(true)
							}}
						/>
					</Grid>
					<Grid item xs={12} sm={4}>
						<Autocomplete
							options={orgMemberNames}
							readOnly={sectionDisabled}
							noOptionsText="No organisation members set"
							renderInput={params => (
								<FormField
									{...params}
									label="Organisation Member Name"
									InputLabelProps={{ shrink: true }}
									error={errorVisible && !localForm.form.details?.orgMember}
									helperText={
										errorVisible && !localForm.form.details?.orgMember
											? 'Booking Phone Number is required'
											: ''
									}
								/>
							)}
							value={localForm.form.details?.orgMember?.name ?? null}
							onChange={(_, newValue) => {
								const memberDetails = memberNameMapping[newValue]
								setLocalForm(prev => ({
									...prev,
									form: {
										...prev.form,
										details: {
											...prev.form.details,
											orgMember: {
												...memberDetails,
												name: newValue,
											},
										},
									},
								}))
								setFormEdited(true)
							}}
						/>
					</Grid>
					{!isNew && localForm.form.details.orgMember && (
						<Grid item xs={12} sm={4} alignItems={'center'} display="flex">
							<MDButton
								variant="text"
								disabled={!localForm.form.details.orgMember.id}
								color="info"
								onClick={() => {
									navigate(
										`${ROUTE_PATHS.ORGANISATION_MEMBER.BASE}/${localForm.form.details.orgMember.id}`
									)
								}}
							>
								View Member Profile&nbsp;&nbsp;&nbsp;
								<Icon>arrow_circle_right</Icon>
							</MDButton>
						</Grid>
					)}
					<Grid item xs={12}>
						<TextField
							disabled={sectionDisabled}
							label={`Details Of Incident`}
							multiline
							rows={5} // Adjust number of rows for the size of the textarea
							variant="outlined"
							placeholder="Please provide a detailed description of the incident."
							InputLabelProps={{ shrink: true }}
							fullWidth
							value={localForm.form.details?.incidentDetails ?? ''}
							onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
								handleFormUpdate(e.target.value, 'incidentDetails')
							}}
							error={
								showError &&
								localForm.stage.current <= STAGE &&
								!localForm.form.details?.incidentDetails
							}
							helperText={
								showError &&
								localForm.stage.min <= STAGE &&
								!localForm.form.details?.incidentDetails
									? 'Details of Incident is required'
									: ''
							}
						/>
					</Grid>
					<Grid item xs={12}>
						<MDTypography variant="h6">Attachments:</MDTypography>
					</Grid>
					{localForm.form.details.attachments?.map((item, index) => (
						<Grid item xs={12} key={index}>
							<MDBox
								display={'flex'}
								alignItems={'center'}
								justifyContent={'space-between'}
								color="secondary"
							>
								<MDBox display={'flex'} alignItems={'center'} flexDirection={'row'}>
									<MDTypography
										onClick={() => {
											if (item.type === 'local') {
												handleLocalDownload(item.file)
											} else {
												handleRemoteDownload(item.url, getFilenameFromFirebaseUrl(item.url))
											}
										}}
										variant="button"
										color="secondary"
										fontWeight="medium"
										sx={{
											cursor: 'pointer',
											textDecoration: 'none',
											'&:hover': {
												textDecoration: 'underline',
											},
										}}
									>
										<i>
											{item.type === 'local'
												? item.file.name
												: getFilenameFromFirebaseUrl(item.url)}
										</i>
									</MDTypography>
									{item.type === 'local' && (
										<Icon fontSize={'small'} color="secondary" sx={{ ml: 2, fontSize: 12 }}>
											cloud_upload
										</Icon>
									)}
									{item.type === 'remote' && item.delete && (
										<MDTypography variant={'caption'} color="error" sx={{ ml: 2 }}>
											<i>Marked for deletion</i>
										</MDTypography>
									)}
								</MDBox>
								<IconButton
									size="small"
									disabled={sectionDisabled}
									color="error"
									onClick={() => {
										if (item.type === 'local') {
											deleteLocalAttachment(item.file)
										} else {
											markRemoteAttachmentForDeletion(index)
										}
									}}
								>
									<Icon fontSize={'small'} color="error" sx={{ ml: 1 }}>
										delete_outline
									</Icon>
								</IconButton>
							</MDBox>
						</Grid>
					))}
					<Grid item xs={12} sm={8}>
						<MDButton component="label" color="info" variant="outlined" disabled={sectionDisabled}>
							<MDInput
								accept={ALLOWED_ATTACHMENT_INPUT_TYPES}
								type="file"
								id="upload-attachment"
								style={{ display: 'none' }}
								onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleFileUpload(e)}
							/>
							Upload Attachment&nbsp;
							<Icon>upload</Icon>
						</MDButton>
						{uploadError && (
							<MDTypography variant="caption" color="error" mt={2} display="block">
								{uploadError}
							</MDTypography>
						)}
					</Grid>
				</Grid>
			</MDBox>
		</Card>
	)
}

export default Details
